/**
 * Cockpit Phase 6 — the closed `cockpit-library` component contract registry.
 *
 * COCKPIT-PLAN §4 ("The closed component library — the lever that makes a local
 * model reliable"). This is the SINGLE source of truth for the ~25 typed
 * component contracts the authoring pipeline composes from and the publish gates
 * lint against. It is intentionally browser-safe (pure Zod + plain data, zero
 * Node imports) so all three consumers share it:
 *
 *   - @argusai/api    — the publish gates (G1 component-lint, G2 a11y) walk a
 *                       CockpitSpec tree and validate each node's props against
 *                       the matching contract's `propsSchema`.
 *   - @argusai/web    — the iframe runtime renderer maps a node's `component`
 *                       slug to a real renderer; the whole catalog is fully
 *                       rendered on BOTH the React preview + vanilla iframe paths.
 *   - @argusai/shared — the CockpitSpec schema (`cockpit-spec.ts`) references the
 *                       registry to reject any node whose component is not in the
 *                       catalog.
 *
 * RULE (COCKPIT-PLAN §4): every generated cockpit composes ONLY from this
 * library. Zero raw `<div>`, zero inline styles, zero `dangerouslySetInnerHTML`.
 * "Only catalog components" is enforced at 4 layers: prompt (soft) → parse
 * (hard, the CockpitSpec schema) → compile (the fixed renderer) → publish (the
 * G1 lint gate → critical → rejected).
 *
 * Each contract is a ROW: { slug, category, propsSchema, bindingSlots,
 * actionSlots, a11yContract, classificationAware, goldenFixtures }. Per
 * CLAUDE.md §20.6 the TypeScript types are derived from the Zod schemas, never
 * the other way around.
 */

import { z } from 'zod';
import { COCKPIT_ICON_NAMES } from './cockpit-icons.js';

/* ─── Category + slot vocab ─────────────────────────────────────────────── */

/**
 * The six component categories (COCKPIT-PLAN §4). Drives the authoring picker
 * grouping and lets gates reason about a node's role (e.g. only `live` + `data`
 * components legally consume a subscribe binding).
 */
export const CockpitComponentCategorySchema = z.enum([
  'layout',
  'display',
  'data',
  'input',
  'action',
  'live',
]);
export type CockpitComponentCategory = z.infer<
  typeof CockpitComponentCategorySchema
>;

/**
 * A binding slot a component exposes. `kind` is tagged so the gates can reject a
 * mismatched binding (e.g. an `action`-kind binding wired into a `read` slot).
 *   - read    — the slot consumes a gateway/tables read binding (data to show).
 *   - state   — the slot consumes a `cockpit_state` / `tables` local read.
 *   - subscribe — the slot is wired to a poll-subscribe binding (live updates).
 */
export const BindingSlotKindSchema = z.enum(['read', 'state', 'subscribe']);
export type BindingSlotKind = z.infer<typeof BindingSlotKindSchema>;

export const BindingSlotSchema = z.object({
  /** The prop name on the node that carries the binding ref. */
  prop: z.string().min(1).max(60),
  kind: BindingSlotKindSchema,
  /** True when the slot must be filled for the component to render meaningfully. */
  required: z.boolean(),
});
export type BindingSlot = z.infer<typeof BindingSlotSchema>;

/**
 * An action slot a component exposes (a button / inline action). Every action
 * slot is approval-gated: the bound action's `approvalGating` is enforced at the
 * execute-action endpoint, NEVER by the component. The slot only declares that
 * the component CAN carry an action ref.
 */
export const ActionSlotSchema = z.object({
  /** The prop name on the node that carries the action ref (actionId). */
  prop: z.string().min(1).max(60),
  /** True when the action slot must be filled. */
  required: z.boolean(),
});
export type ActionSlot = z.infer<typeof ActionSlotSchema>;

/**
 * The a11y contract a component promises. Used by G2 (a11y gate) as the
 * deterministic fallback when no headless browser / axe-core is available.
 */
export const A11yContractSchema = z.object({
  /** ARIA role the rendered root must carry (informational + gate check). */
  role: z.string().min(1).max(60).optional(),
  /** Prop names whose values become the accessible name — must be non-empty. */
  labelledByProps: z.array(z.string().min(1).max(60)).max(8).default([]),
  /** True when the component is interactive and must be keyboard-reachable. */
  keyboardInteractive: z.boolean().default(false),
});
export type A11yContract = z.infer<typeof A11yContractSchema>;

/* ─── The contract row ──────────────────────────────────────────────────── */

/**
 * One component contract. `propsSchema` is a real Zod schema validated by the
 * gates against each node's props. Because Zod schemas are not serialisable, the
 * registry is a code module (not a DB row) — it is the platform-canonical
 * vocabulary, versioned with the codebase, the same way the helper-mutation
 * tool catalog is.
 */
export interface CockpitComponentContract {
  slug: string;
  category: CockpitComponentCategory;
  /** Human label for the authoring picker. */
  label: string;
  /** One-line description fed into the Stage-A authoring prompt catalog. */
  description: string;
  /** Zod schema for the node's `props`. Gates `.safeParse()` against this. */
  propsSchema: z.ZodTypeAny;
  /** Binding slots the component exposes (kind-tagged). */
  bindingSlots: BindingSlot[];
  /** Action slots the component exposes (approval-gated). */
  actionSlots: ActionSlot[];
  /**
   * NESTED action-ref paths — for components that carry action refs INSIDE an
   * array prop (not as a top-level prop). `actionSlots` only describes top-level
   * action-ref props (e.g. ActionButton.action), so the publish-time G1 gate
   * (`validateNodeTree`) cannot see refs buried in `props.buttons[].action`.
   * Each entry is an `array[].field` path the gate walks to validate every
   * nested ref against `declaredActionIds` (→ ACTION_REF_NOT_DECLARED). Without
   * this, a ButtonGroup could publish with an undeclared action that 404s on
   * click — fail-closed at runtime, but a false guarantee + a dead button.
   *
   * Format: `'<arrayProp>[].<refField>'` (single level of array nesting, which
   * covers every catalog component that nests refs). Omitted = no nested refs.
   */
  nestedActionPaths?: string[];
  /**
   * NESTED binding-ref paths — the binding analogue of `nestedActionPaths` (refs
   * to declared data bindings buried inside an array prop). Validated against
   * `declaredBindingIds` (→ BINDING_REF_NOT_DECLARED). No catalog component nests
   * binding refs today, but the gate honours this for forward-compatibility.
   */
  nestedBindingPaths?: string[];
  a11yContract: A11yContract;
  /** True when the component renders classification-sensitive data. */
  classificationAware: boolean;
  /** True when this component has a real React renderer (vs contract+stub). */
  rendered: boolean;
  /**
   * Golden fixture prop-bags — valid props the G3 screenshot-diff gate and the
   * unit tests use as the known-good baseline. At least one per component.
   */
  goldenFixtures: Array<Record<string, unknown>>;
}

/* ─── Shared prop fragments ─────────────────────────────────────────────── */

/** The status palette (COCKPIT-PLAN §4): critical=red, warning=amber, normal=slate, success=green. */
export const CockpitStatusToneSchema = z.enum([
  'normal',
  'success',
  'warning',
  'critical',
  'info',
]);
export type CockpitStatusTone = z.infer<typeof CockpitStatusToneSchema>;

/** A reference to a declared data binding by its manifest id. */
const BindingRefSchema = z.string().min(1).max(120);
/** A reference to a declared action by its actionId. */
const ActionRefSchema = z.string().min(1).max(120);

/**
 * The chart families the `Chart` component supports. Both renderers draw these:
 * React via Recharts, the vanilla iframe path via hand-computed inline SVG.
 *   line/area/bar — cartesian; sparkline — compact line, no axes; scatter —
 *   points; pie/donut — categorical share; gauge — single 0..max needle.
 */
export const CockpitChartTypeSchema = z.enum([
  'line',
  'area',
  'bar',
  'pie',
  'donut',
  'gauge',
  'sparkline',
  'scatter',
]);
export type CockpitChartType = z.infer<typeof CockpitChartTypeSchema>;

/** A status-palette colour key for a chart series / legend swatch. */
const ChartColorSchema = z.enum(['normal', 'success', 'warning', 'critical', 'info']);

/** The named icon set the `Icon` component draws from (the air-gap-safe curated set). */
export const CockpitIconNameSchema = z.enum(
  COCKPIT_ICON_NAMES as [string, ...string[]],
);

/* ─── The component contracts ───────────────────────────────────────────── */
//
// Ordering: layout → display → data → input → action → live, then the
// feat/cockpit comprehensive palette (charts, maps, icons, controls). EVERY
// contract below is `rendered:true` and ships a REAL renderer in BOTH the React
// authoring preview (Recharts + lucide + shadcn) AND the vanilla iframe runtime
// (hand-drawn DOM/SVG, air-gap-safe). The `rendered` flag stays in the contract
// shape for forward-compatibility (a future slug added without a renderer would
// set it false), but the catalog is currently fully rendered on both paths.

const CONTRACTS: CockpitComponentContract[] = [
  /* ── layout ──────────────────────────────────────────────────────────── */
  {
    slug: 'CockpitContainer',
    category: 'layout',
    label: 'Cockpit Container',
    description:
      'Root layout shell of every cockpit. Holds a header, a vertical stack of child sections, and an optional resident-agent side panel. The ONLY legal root node.',
    propsSchema: z
      .object({
        title: z.string().min(1).max(160),
        subtitle: z.string().max(240).optional(),
        density: z.enum(['comfortable', 'compact']).default('comfortable'),
      })
      .strict(),
    bindingSlots: [],
    actionSlots: [],
    a11yContract: { role: 'main', labelledByProps: ['title'], keyboardInteractive: false },
    classificationAware: false,
    rendered: true,
    goldenFixtures: [{ title: 'Asset Health — Pump P-401' }],
  },
  {
    slug: 'CockpitHeader',
    category: 'layout',
    label: 'Header',
    description:
      'Title bar with optional live timestamp + status pill. Use at the top of a section group.',
    propsSchema: z
      .object({
        title: z.string().min(1).max(160),
        showTimestamp: z.boolean().default(true),
        tone: CockpitStatusToneSchema.default('normal'),
      })
      .strict(),
    bindingSlots: [],
    actionSlots: [],
    a11yContract: { role: 'banner', labelledByProps: ['title'], keyboardInteractive: false },
    classificationAware: false,
    rendered: true,
    goldenFixtures: [{ title: 'Live status', showTimestamp: true, tone: 'normal' }],
  },
  {
    slug: 'CockpitTabs',
    category: 'layout',
    label: 'Tabs',
    description:
      'Tabbed container; each child section is a tab panel. Use to split a busy cockpit into focused views.',
    propsSchema: z
      .object({
        tabs: z
          .array(z.object({ id: z.string().min(1).max(60), label: z.string().min(1).max(60) }))
          .min(1)
          .max(8),
        defaultTabId: z.string().min(1).max(60).optional(),
      })
      .strict(),
    bindingSlots: [],
    actionSlots: [],
    a11yContract: { role: 'tablist', labelledByProps: [], keyboardInteractive: true },
    classificationAware: false,
    rendered: true,
    goldenFixtures: [
      { tabs: [{ id: 'overview', label: 'Overview' }, { id: 'detail', label: 'Detail' }] },
    ],
  },
  {
    slug: 'CockpitSidePanel',
    category: 'layout',
    label: 'Side Panel',
    description:
      'Collapsible right-hand panel. Reserved for the resident agent or a detail inspector.',
    propsSchema: z
      .object({
        title: z.string().min(1).max(120),
        defaultOpen: z.boolean().default(true),
        width: z.enum(['sm', 'md', 'lg']).default('md'),
      })
      .strict(),
    bindingSlots: [],
    actionSlots: [],
    a11yContract: { role: 'complementary', labelledByProps: ['title'], keyboardInteractive: true },
    classificationAware: false,
    rendered: true,
    goldenFixtures: [{ title: 'Details' }],
  },

  /* ── display ─────────────────────────────────────────────────────────── */
  {
    slug: 'MetricCard',
    category: 'display',
    label: 'Metric Card',
    description:
      'A single headline KPI: a label, a big value, an optional unit + delta + status tone. Bind `valueBinding` to a read for live data.',
    propsSchema: z
      .object({
        label: z.string().min(1).max(80),
        value: z.union([z.string().max(40), z.number()]).optional(),
        unit: z.string().max(24).optional(),
        delta: z
          .object({
            value: z.union([z.string().max(24), z.number()]),
            direction: z.enum(['up', 'down', 'flat']),
            tone: CockpitStatusToneSchema.default('normal'),
          })
          .optional(),
        tone: CockpitStatusToneSchema.default('normal'),
        valueBinding: BindingRefSchema.optional(),
        valuePath: z.string().max(200).optional(),
      })
      .strict(),
    bindingSlots: [{ prop: 'valueBinding', kind: 'read', required: false }],
    actionSlots: [],
    a11yContract: { role: 'group', labelledByProps: ['label'], keyboardInteractive: false },
    classificationAware: true,
    rendered: true,
    goldenFixtures: [
      { label: 'Vibration', value: 3.2, unit: 'mm/s', tone: 'warning' },
      { label: 'Uptime', value: '99.4', unit: '%', tone: 'success' },
    ],
  },
  {
    slug: 'StatusPill',
    category: 'display',
    label: 'Status Pill',
    description: 'A small coloured status chip with a text label and a tone.',
    propsSchema: z
      .object({
        label: z.string().min(1).max(48),
        tone: CockpitStatusToneSchema.default('normal'),
      })
      .strict(),
    bindingSlots: [],
    actionSlots: [],
    a11yContract: { role: 'status', labelledByProps: ['label'], keyboardInteractive: false },
    classificationAware: false,
    rendered: true,
    goldenFixtures: [{ label: 'Operational', tone: 'success' }],
  },
  {
    slug: 'AlertBanner',
    category: 'display',
    label: 'Alert Banner',
    description:
      'A full-width banner for a critical/warning/info message. Use for alarms, degraded states, or empty/error notices.',
    propsSchema: z
      .object({
        tone: CockpitStatusToneSchema.default('info'),
        title: z.string().min(1).max(120),
        message: z.string().max(400).optional(),
        dismissable: z.boolean().default(false),
      })
      .strict(),
    bindingSlots: [],
    actionSlots: [],
    a11yContract: { role: 'alert', labelledByProps: ['title'], keyboardInteractive: false },
    classificationAware: false,
    rendered: true,
    goldenFixtures: [
      { tone: 'critical', title: 'High vibration', message: 'P-401 exceeded the 4.5 mm/s alarm limit.' },
    ],
  },
  {
    slug: 'DetailPanel',
    category: 'display',
    label: 'Detail Panel',
    description:
      'A key/value detail view for a single selected entity. Bind `recordBinding` to a read; `fields` names which keys to show.',
    propsSchema: z
      .object({
        title: z.string().min(1).max(120),
        recordBinding: BindingRefSchema.optional(),
        fields: z
          .array(z.object({ key: z.string().min(1).max(80), label: z.string().min(1).max(80) }))
          .max(40)
          .default([]),
      })
      .strict(),
    bindingSlots: [{ prop: 'recordBinding', kind: 'read', required: false }],
    actionSlots: [],
    a11yContract: { role: 'group', labelledByProps: ['title'], keyboardInteractive: false },
    classificationAware: true,
    rendered: true,
    goldenFixtures: [
      { title: 'Work order', fields: [{ key: 'id', label: 'ID' }, { key: 'status', label: 'Status' }] },
    ],
  },

  /* ── data ────────────────────────────────────────────────────────────── */
  {
    slug: 'StatusCardGrid',
    category: 'data',
    label: 'Status Card Grid',
    description:
      'A responsive grid of status cards driven by a read binding returning an array. Each card shows a title, a value, and a tone derived from `toneField`.',
    propsSchema: z
      .object({
        title: z.string().max(120).optional(),
        itemsBinding: BindingRefSchema.optional(),
        titleField: z.string().min(1).max(80).default('name'),
        valueField: z.string().min(1).max(80).default('value'),
        toneField: z.string().max(80).optional(),
        columns: z.number().int().min(1).max(6).default(3),
        items: z
          .array(
            z.object({
              title: z.string().max(80),
              value: z.union([z.string().max(40), z.number()]).optional(),
              tone: CockpitStatusToneSchema.optional(),
            }),
          )
          .max(60)
          .optional(),
      })
      .strict(),
    bindingSlots: [{ prop: 'itemsBinding', kind: 'read', required: false }],
    actionSlots: [],
    a11yContract: { role: 'list', labelledByProps: ['title'], keyboardInteractive: false },
    classificationAware: true,
    rendered: true,
    goldenFixtures: [
      {
        title: 'Asset health',
        items: [
          { title: 'P-401', value: 'OK', tone: 'success' },
          { title: 'P-402', value: 'Alarm', tone: 'critical' },
        ],
      },
    ],
  },
  {
    slug: 'DataTable',
    category: 'data',
    label: 'Data Table',
    description:
      'A sortable table driven by a read or state binding returning rows. `columns` declares the visible fields. Supports an optional per-row action via `rowAction`.',
    propsSchema: z
      .object({
        title: z.string().max(120).optional(),
        rowsBinding: BindingRefSchema.optional(),
        columns: z
          .array(
            z.object({
              key: z.string().min(1).max(80),
              label: z.string().min(1).max(80),
              align: z.enum(['left', 'right', 'center']).default('left'),
            }),
          )
          .min(1)
          .max(24),
        rowKeyField: z.string().min(1).max(80).default('id'),
        rowAction: ActionRefSchema.optional(),
        rowActionLabel: z.string().max(40).optional(),
        emptyMessage: z.string().max(200).default('No rows yet.'),
        pageSize: z.number().int().min(5).max(200).default(50),
        rows: z.array(z.record(z.string(), z.unknown())).max(2000).optional(),
      })
      .strict(),
    bindingSlots: [
      { prop: 'rowsBinding', kind: 'read', required: false },
    ],
    actionSlots: [{ prop: 'rowAction', required: false }],
    a11yContract: { role: 'table', labelledByProps: ['title'], keyboardInteractive: true },
    classificationAware: true,
    rendered: true,
    goldenFixtures: [
      {
        title: 'Open work orders',
        columns: [
          { key: 'id', label: 'WO #' },
          { key: 'asset', label: 'Asset' },
          { key: 'status', label: 'Status' },
        ],
        rows: [
          { id: 'WO-1001', asset: 'P-401', status: 'Open' },
          { id: 'WO-1002', asset: 'P-402', status: 'In progress' },
        ],
      },
    ],
  },
  {
    slug: 'KanbanBoard',
    category: 'data',
    label: 'Kanban Board',
    description:
      'A column board grouping rows by a status field. Bind `rowsBinding`; `groupField` names the column key. Read-only V1 (drag is V2).',
    propsSchema: z
      .object({
        title: z.string().max(120).optional(),
        rowsBinding: BindingRefSchema.optional(),
        groupField: z.string().min(1).max(80),
        cardTitleField: z.string().min(1).max(80).default('title'),
        columns: z.array(z.string().min(1).max(60)).min(1).max(8),
      })
      .strict(),
    bindingSlots: [{ prop: 'rowsBinding', kind: 'read', required: false }],
    actionSlots: [],
    a11yContract: { role: 'list', labelledByProps: ['title'], keyboardInteractive: true },
    classificationAware: true,
    rendered: true,
    goldenFixtures: [
      { groupField: 'status', columns: ['Open', 'In progress', 'Done'] },
    ],
  },
  {
    slug: 'Timeline',
    category: 'data',
    label: 'Timeline',
    description:
      'A vertical chronological list of events driven by a read binding. Each event has a timestamp, a title, and a tone.',
    propsSchema: z
      .object({
        title: z.string().max(120).optional(),
        eventsBinding: BindingRefSchema.optional(),
        timestampField: z.string().min(1).max(80).default('ts'),
        titleField: z.string().min(1).max(80).default('title'),
        toneField: z.string().max(80).optional(),
      })
      .strict(),
    bindingSlots: [{ prop: 'eventsBinding', kind: 'read', required: false }],
    actionSlots: [],
    a11yContract: { role: 'list', labelledByProps: ['title'], keyboardInteractive: false },
    classificationAware: true,
    rendered: true,
    goldenFixtures: [{ title: 'Alarm history', timestampField: 'ts', titleField: 'message' }],
  },
  {
    slug: 'ChartCard',
    category: 'data',
    label: 'Chart Card',
    description:
      'A titled line/area/bar chart driven by a read binding returning a series. `xField`/`yField` name the axes. Uses the platform Recharts wrapper.',
    propsSchema: z
      .object({
        title: z.string().min(1).max(120),
        seriesBinding: BindingRefSchema.optional(),
        chartType: z.enum(['line', 'area', 'bar']).default('line'),
        xField: z.string().min(1).max(80).default('x'),
        yField: z.string().min(1).max(80).default('y'),
        unit: z.string().max(24).optional(),
        tone: CockpitStatusToneSchema.default('info'),
        series: z
          .array(z.object({ x: z.union([z.string(), z.number()]), y: z.number() }))
          .max(2000)
          .optional(),
      })
      .strict(),
    bindingSlots: [{ prop: 'seriesBinding', kind: 'read', required: false }],
    actionSlots: [],
    a11yContract: { role: 'img', labelledByProps: ['title'], keyboardInteractive: false },
    classificationAware: true,
    rendered: true,
    goldenFixtures: [
      {
        title: 'Vibration trend (24h)',
        chartType: 'line',
        unit: 'mm/s',
        series: [
          { x: '00:00', y: 2.1 },
          { x: '06:00', y: 2.8 },
          { x: '12:00', y: 3.4 },
          { x: '18:00', y: 3.2 },
        ],
      },
    ],
  },

  /* ── input ───────────────────────────────────────────────────────────── */
  {
    slug: 'SearchBar',
    category: 'input',
    label: 'Search Bar',
    description:
      'A text input that filters a bound table/grid client-side. Declares which sibling component id it filters via `targetId`.',
    propsSchema: z
      .object({
        placeholder: z.string().max(120).default('Search…'),
        targetId: z.string().min(1).max(120),
      })
      .strict(),
    bindingSlots: [],
    actionSlots: [],
    a11yContract: { role: 'search', labelledByProps: ['placeholder'], keyboardInteractive: true },
    classificationAware: false,
    rendered: true,
    goldenFixtures: [{ placeholder: 'Search work orders', targetId: 'wo-table' }],
  },
  {
    slug: 'FilterChips',
    category: 'input',
    label: 'Filter Chips',
    description:
      'A row of toggleable filter chips that scope a bound table/grid. `targetId` names the component they filter.',
    propsSchema: z
      .object({
        targetId: z.string().min(1).max(120),
        field: z.string().min(1).max(80),
        chips: z
          .array(z.object({ label: z.string().min(1).max(40), value: z.string().min(1).max(80) }))
          .min(1)
          .max(12),
      })
      .strict(),
    bindingSlots: [],
    actionSlots: [],
    a11yContract: { role: 'group', labelledByProps: ['field'], keyboardInteractive: true },
    classificationAware: false,
    rendered: true,
    goldenFixtures: [
      { targetId: 'wo-table', field: 'status', chips: [{ label: 'Open', value: 'open' }] },
    ],
  },
  {
    slug: 'RichTextEditor',
    category: 'input',
    label: 'Rich Text Editor',
    description:
      'A constrained rich-text input for notes/comments. Bound to a cockpit_state field via `stateBinding`. No raw HTML — markdown subset only.',
    propsSchema: z
      .object({
        label: z.string().min(1).max(80),
        stateBinding: BindingRefSchema.optional(),
        placeholder: z.string().max(200).optional(),
        maxLength: z.number().int().min(1).max(20000).default(4000),
      })
      .strict(),
    bindingSlots: [{ prop: 'stateBinding', kind: 'state', required: false }],
    actionSlots: [],
    a11yContract: { role: 'textbox', labelledByProps: ['label'], keyboardInteractive: true },
    classificationAware: true,
    rendered: true,
    goldenFixtures: [{ label: 'Shift note' }],
  },

  /* ── action ──────────────────────────────────────────────────────────── */
  {
    slug: 'ActionButton',
    category: 'action',
    label: 'Action Button',
    description:
      'A button that triggers a declared, approval-gated action. Set `action` to the actionId. The button never mints authorization — the host execute endpoint does. Gating (read_only / approval_required / auto_execute) is enforced server-side.',
    propsSchema: z
      .object({
        label: z.string().min(1).max(60),
        action: ActionRefSchema,
        tone: z.enum(['primary', 'secondary', 'danger']).default('primary'),
        confirm: z.boolean().default(false),
        icon: z.string().max(40).optional(),
      })
      .strict(),
    bindingSlots: [],
    actionSlots: [{ prop: 'action', required: true }],
    a11yContract: { role: 'button', labelledByProps: ['label'], keyboardInteractive: true },
    classificationAware: false,
    rendered: true,
    goldenFixtures: [
      { label: 'Create work order', action: 'create_wo', tone: 'primary' },
      { label: 'Reassign', action: 'reassign_wo', tone: 'secondary' },
    ],
  },
  {
    slug: 'InlineActions',
    category: 'action',
    label: 'Inline Actions',
    description:
      'A compact cluster of small action buttons (e.g. approve / dismiss) for a row or card context. Each entry references an actionId.',
    propsSchema: z
      .object({
        actions: z
          .array(
            z.object({
              label: z.string().min(1).max(40),
              action: ActionRefSchema,
              tone: z.enum(['primary', 'secondary', 'danger']).default('secondary'),
            }),
          )
          .min(1)
          .max(6),
      })
      .strict(),
    bindingSlots: [],
    actionSlots: [],
    // The action refs live in `actions[].action`, not a top-level prop — declare
    // the nested path so the G1 gate validates each one against declaredActionIds.
    nestedActionPaths: ['actions[].action'],
    a11yContract: { role: 'group', labelledByProps: [], keyboardInteractive: true },
    classificationAware: false,
    rendered: true,
    goldenFixtures: [
      { actions: [{ label: 'Approve', action: 'approve_x', tone: 'primary' }] },
    ],
  },

  /* ── live ────────────────────────────────────────────────────────────── */
  {
    slug: 'ResidentAgentPanel',
    category: 'live',
    label: 'Resident Agent Panel',
    description:
      'The copilot side-panel: a chat with the cockpit-scoped resident agent plus up to 3 suggestion chips. The resident proposes gated actions; the human approves. NEVER auto-executes.',
    propsSchema: z
      .object({
        title: z.string().min(1).max(80).default('Argus'),
        greeting: z.string().max(280).optional(),
        showChips: z.boolean().default(true),
      })
      .strict(),
    bindingSlots: [],
    actionSlots: [],
    a11yContract: { role: 'complementary', labelledByProps: ['title'], keyboardInteractive: true },
    classificationAware: true,
    rendered: true,
    goldenFixtures: [{ title: 'Argus', greeting: 'I am watching P-401. Ask me anything.' }],
  },
  {
    slug: 'LiveIndicator',
    category: 'live',
    label: 'Live Indicator',
    description:
      'A small "live / last updated just now" badge wired to a subscribe binding. Confirms the adaptive-poll freshness to the operator.',
    propsSchema: z
      .object({
        label: z.string().max(60).default('Live'),
        subscribeBinding: BindingRefSchema.optional(),
      })
      .strict(),
    bindingSlots: [{ prop: 'subscribeBinding', kind: 'subscribe', required: false }],
    actionSlots: [],
    a11yContract: { role: 'status', labelledByProps: ['label'], keyboardInteractive: false },
    classificationAware: false,
    rendered: true,
    goldenFixtures: [{ label: 'Live' }],
  },
  {
    slug: 'NotificationFeed',
    category: 'live',
    label: 'Notification Feed',
    description:
      'A scrolling feed of fresh rows arriving over a subscribe binding (poll-driven, never LISTEN/NOTIFY). New rows fade in with a "just now" timestamp.',
    propsSchema: z
      .object({
        title: z.string().max(120).optional(),
        feedBinding: BindingRefSchema.optional(),
        subscribeBinding: BindingRefSchema.optional(),
        titleField: z.string().min(1).max(80).default('title'),
        timestampField: z.string().min(1).max(80).default('ts'),
        maxItems: z.number().int().min(1).max(200).default(50),
      })
      .strict(),
    bindingSlots: [
      { prop: 'feedBinding', kind: 'read', required: false },
      { prop: 'subscribeBinding', kind: 'subscribe', required: false },
    ],
    actionSlots: [],
    a11yContract: { role: 'log', labelledByProps: ['title'], keyboardInteractive: false },
    classificationAware: true,
    rendered: true,
    goldenFixtures: [{ title: 'Incoming alarms' }],
  },

  /* ═══ feat/cockpit comprehensive palette ════════════════════════════════ */
  // Charts, maps, icons + the round-out controls. Each ships a REAL renderer in
  // BOTH the React preview (Recharts/lucide) and the vanilla iframe (hand-drawn
  // inline SVG, air-gap-safe). "tables, graphs, MAPS, buttons, ICONS, kanban".

  /* ── data: Chart (the full chart family) ───────────────────────────────── */
  {
    slug: 'Chart',
    category: 'data',
    label: 'Chart',
    description:
      'A multi-type chart: line, area, bar, pie, donut, gauge, sparkline, or scatter. Bind `dataBinding` to a read returning an array of points; `xKey` names the category/x field and `yKeys` the value series. React draws via Recharts; the iframe draws hand-computed inline SVG (axes, gridlines, status-palette colours, legend). Use for any quantitative trend or breakdown.',
    propsSchema: z
      .object({
        title: z.string().max(120).optional(),
        chartType: CockpitChartTypeSchema.default('line'),
        dataBinding: BindingRefSchema.optional(),
        xKey: z.string().min(1).max(80).default('x'),
        yKeys: z.array(z.string().min(1).max(80)).min(1).max(8).default(['y']),
        colors: z.array(ChartColorSchema).max(8).optional(),
        unit: z.string().max(24).optional(),
        legend: z.boolean().default(true),
        /** Gauge-only: the full-scale value the needle is measured against. */
        max: z.number().optional(),
        /** Inline data for authoring previews / static cockpits (array of point objects). */
        data: z.array(z.record(z.string(), z.unknown())).max(2000).optional(),
      })
      .strict(),
    bindingSlots: [{ prop: 'dataBinding', kind: 'read', required: false }],
    actionSlots: [],
    a11yContract: { role: 'img', labelledByProps: ['title'], keyboardInteractive: false },
    classificationAware: true,
    rendered: true,
    goldenFixtures: [
      {
        title: 'Vibration trend (24h)',
        chartType: 'line',
        xKey: 'ts',
        yKeys: ['value'],
        unit: 'mm/s',
        colors: ['info'],
        data: [
          { ts: '00:00', value: 2.1 },
          { ts: '06:00', value: 2.8 },
          { ts: '12:00', value: 3.4 },
          { ts: '18:00', value: 3.2 },
        ],
      },
      {
        title: 'Work orders by status',
        chartType: 'donut',
        xKey: 'status',
        yKeys: ['count'],
        legend: true,
        colors: ['success', 'warning', 'critical'],
        data: [
          { status: 'Done', count: 12 },
          { status: 'Open', count: 5 },
          { status: 'Blocked', count: 2 },
        ],
      },
      {
        title: 'Tank level',
        chartType: 'gauge',
        xKey: 'label',
        yKeys: ['value'],
        unit: '%',
        max: 100,
        colors: ['warning'],
        data: [{ label: 'Level', value: 72 }],
      },
    ],
  },

  /* ── data: MapView (air-gap-safe inline-SVG geospatial) ────────────────── */
  {
    slug: 'MapView',
    category: 'data',
    label: 'Map',
    description:
      'A geospatial map that plots status-coloured markers (and optional region polygons) on an inline SVG equirectangular projection. AIR-GAP SAFE: no tile server, no Leaflet/CDN — markers + regions are drawn from author/binding data only. Bind `pointsBinding` to a read returning [{lat,lng,label,status}]; pass `regions` as GeoJSON-ish polygons of [lng,lat] rings. Use for asset/site/sensor location overviews.',
    propsSchema: z
      .object({
        title: z.string().max(120).optional(),
        pointsBinding: BindingRefSchema.optional(),
        latKey: z.string().min(1).max(80).default('lat'),
        lngKey: z.string().min(1).max(80).default('lng'),
        labelKey: z.string().min(1).max(80).default('label'),
        statusKey: z.string().max(80).optional(),
        /** Optional explicit bounding box [minLng, minLat, maxLng, maxLat]; auto-fit if omitted. */
        bbox: z.tuple([z.number(), z.number(), z.number(), z.number()]).optional(),
        /** Optional region overlays: each a ring of [lng, lat] pairs + a tone. */
        regions: z
          .array(
            z.object({
              label: z.string().max(80).optional(),
              tone: CockpitStatusToneSchema.default('info'),
              ring: z.array(z.tuple([z.number(), z.number()])).min(3).max(400),
            }),
          )
          .max(40)
          .optional(),
        /** Inline points for authoring previews / static cockpits. */
        points: z
          .array(
            z.object({
              lat: z.number(),
              lng: z.number(),
              label: z.string().max(80).optional(),
              status: CockpitStatusToneSchema.optional(),
            }),
          )
          .max(500)
          .optional(),
      })
      .strict(),
    bindingSlots: [{ prop: 'pointsBinding', kind: 'read', required: false }],
    actionSlots: [],
    a11yContract: { role: 'img', labelledByProps: ['title'], keyboardInteractive: false },
    classificationAware: true,
    rendered: true,
    goldenFixtures: [
      {
        title: 'Site map',
        points: [
          { lat: 29.76, lng: -95.37, label: 'Houston plant', status: 'success' },
          { lat: 32.78, lng: -96.8, label: 'Dallas depot', status: 'warning' },
          { lat: 30.27, lng: -97.74, label: 'Austin hub', status: 'critical' },
        ],
      },
    ],
  },

  /* ── data: TreeList (collapsible hierarchy) ────────────────────────────── */
  {
    slug: 'TreeList',
    category: 'data',
    label: 'Tree List',
    description:
      'A collapsible hierarchical list (e.g. an asset/location tree). Bind `nodesBinding` to a read returning nested { label, children, status } nodes, or pass `nodes` inline. Each branch toggles open/closed; leaves can carry a status tone. Use for equipment hierarchies, folder trees, BOM structures.',
    propsSchema: z
      .object({
        title: z.string().max(120).optional(),
        nodesBinding: BindingRefSchema.optional(),
        labelField: z.string().min(1).max(80).default('label'),
        childrenField: z.string().min(1).max(80).default('children'),
        toneField: z.string().max(80).optional(),
        defaultExpanded: z.boolean().default(true),
        /** Inline tree for authoring previews / static cockpits. */
        nodes: z.array(z.record(z.string(), z.unknown())).max(500).optional(),
      })
      .strict(),
    bindingSlots: [{ prop: 'nodesBinding', kind: 'read', required: false }],
    actionSlots: [],
    a11yContract: { role: 'tree', labelledByProps: ['title'], keyboardInteractive: true },
    classificationAware: true,
    rendered: true,
    goldenFixtures: [
      {
        title: 'Asset hierarchy',
        nodes: [
          {
            label: 'Boiler Room A',
            children: [
              { label: 'Pump P-401', status: 'warning' },
              { label: 'Pump P-402', status: 'success' },
            ],
          },
          { label: 'Compressor House', children: [{ label: 'Compressor C-101', status: 'success' }] },
        ],
      },
    ],
  },

  /* ── display: Icon ─────────────────────────────────────────────────────── */
  {
    slug: 'Icon',
    category: 'display',
    label: 'Icon',
    description:
      'A single inline-SVG icon from the curated air-gap-safe set (~40 lucide glyphs by name, e.g. "alert-triangle", "gauge", "map-pin"). React renders the matching lucide-react glyph; the iframe hand-draws the same path geometry — no font assets, no CDN. Optional status tone + size. Use as an affordance next to a label, stat, or alert.',
    propsSchema: z
      .object({
        name: CockpitIconNameSchema,
        tone: CockpitStatusToneSchema.default('normal'),
        size: z.enum(['sm', 'md', 'lg']).default('md'),
        /** Accessible label; when omitted the icon is decorative (aria-hidden). */
        label: z.string().max(80).optional(),
      })
      .strict(),
    bindingSlots: [],
    actionSlots: [],
    a11yContract: { role: 'img', labelledByProps: ['label'], keyboardInteractive: false },
    classificationAware: false,
    rendered: true,
    goldenFixtures: [
      { name: 'alert-triangle', tone: 'warning', label: 'Warning' },
      { name: 'gauge', tone: 'info', size: 'lg' },
    ],
  },

  /* ── display: Badge ────────────────────────────────────────────────────── */
  {
    slug: 'Badge',
    category: 'display',
    label: 'Badge',
    description:
      'A compact inline label chip with a status tone and an optional leading icon. Lighter than StatusPill (no dot) — use for counts, tags, severities, categories inline within text or a card header.',
    propsSchema: z
      .object({
        label: z.string().min(1).max(48),
        tone: CockpitStatusToneSchema.default('normal'),
        icon: CockpitIconNameSchema.optional(),
        variant: z.enum(['solid', 'soft', 'outline']).default('soft'),
      })
      .strict(),
    bindingSlots: [],
    actionSlots: [],
    a11yContract: { role: 'status', labelledByProps: ['label'], keyboardInteractive: false },
    classificationAware: false,
    rendered: true,
    goldenFixtures: [
      { label: 'Critical', tone: 'critical', icon: 'alert-triangle', variant: 'soft' },
      { label: '12 open', tone: 'info', variant: 'outline' },
    ],
  },

  /* ── display: ProgressBar ──────────────────────────────────────────────── */
  {
    slug: 'ProgressBar',
    category: 'display',
    label: 'Progress Bar',
    description:
      'A horizontal progress / utilisation bar. `value` over `max` (default 100); the fill colour follows the tone (or thresholds). Bind `valueBinding` to a read for a live percentage. Use for completion %, capacity, fill level, SLA burn.',
    propsSchema: z
      .object({
        label: z.string().max(80).optional(),
        value: z.number().default(0),
        max: z.number().positive().default(100),
        tone: CockpitStatusToneSchema.default('info'),
        showValue: z.boolean().default(true),
        valueBinding: BindingRefSchema.optional(),
        valuePath: z.string().max(200).optional(),
      })
      .strict(),
    bindingSlots: [{ prop: 'valueBinding', kind: 'read', required: false }],
    actionSlots: [],
    a11yContract: { role: 'progressbar', labelledByProps: ['label'], keyboardInteractive: false },
    classificationAware: true,
    rendered: true,
    goldenFixtures: [
      { label: 'Tank level', value: 72, max: 100, tone: 'warning' },
      { label: 'PM completion', value: 18, max: 24, tone: 'success' },
    ],
  },

  /* ── display: Stat (big number + delta) ────────────────────────────────── */
  {
    slug: 'Stat',
    category: 'display',
    label: 'Stat',
    description:
      'A big-number stat with a label, an optional unit, an optional trend delta (with direction + tone) and an optional leading icon. Leaner than MetricCard (no card chrome) — use inside a row of headline numbers. Bind `valueBinding` for live data.',
    propsSchema: z
      .object({
        label: z.string().min(1).max(80),
        value: z.union([z.string().max(40), z.number()]).optional(),
        unit: z.string().max(24).optional(),
        icon: CockpitIconNameSchema.optional(),
        tone: CockpitStatusToneSchema.default('normal'),
        delta: z
          .object({
            value: z.union([z.string().max(24), z.number()]),
            direction: z.enum(['up', 'down', 'flat']),
            tone: CockpitStatusToneSchema.default('normal'),
          })
          .optional(),
        valueBinding: BindingRefSchema.optional(),
        valuePath: z.string().max(200).optional(),
      })
      .strict(),
    bindingSlots: [{ prop: 'valueBinding', kind: 'read', required: false }],
    actionSlots: [],
    a11yContract: { role: 'group', labelledByProps: ['label'], keyboardInteractive: false },
    classificationAware: true,
    rendered: true,
    goldenFixtures: [
      { label: 'Open work orders', value: 42, icon: 'wrench', tone: 'warning', delta: { value: '+6', direction: 'up', tone: 'warning' } },
      { label: 'Uptime', value: '99.4', unit: '%', icon: 'activity', tone: 'success' },
    ],
  },

  /* ── layout: Divider ───────────────────────────────────────────────────── */
  {
    slug: 'Divider',
    category: 'layout',
    label: 'Divider',
    description:
      'A horizontal rule with an optional centred caption — splits a section into labelled bands. Decorative when no label; a semantic separator otherwise.',
    propsSchema: z
      .object({
        label: z.string().max(80).optional(),
        spacing: z.enum(['sm', 'md', 'lg']).default('md'),
      })
      .strict(),
    bindingSlots: [],
    actionSlots: [],
    a11yContract: { role: 'separator', labelledByProps: ['label'], keyboardInteractive: false },
    classificationAware: false,
    rendered: true,
    goldenFixtures: [{ label: 'Diagnostics' }, {}],
  },

  /* ── action: ButtonGroup ───────────────────────────────────────────────── */
  {
    slug: 'ButtonGroup',
    category: 'action',
    label: 'Button Group',
    description:
      'A horizontally-joined cluster of action buttons (each references a declared, approval-gated action). Like InlineActions but visually segmented (shared border). Use for a compact toolbar of related commands (e.g. Acknowledge / Escalate / Snooze).',
    propsSchema: z
      .object({
        buttons: z
          .array(
            z.object({
              label: z.string().min(1).max(40),
              action: ActionRefSchema,
              icon: CockpitIconNameSchema.optional(),
              tone: z.enum(['primary', 'secondary', 'danger']).default('secondary'),
            }),
          )
          .min(1)
          .max(8),
      })
      .strict(),
    bindingSlots: [],
    actionSlots: [],
    // The action refs live in `buttons[].action`, not a top-level prop — declare
    // the nested path so the G1 gate validates each one against declaredActionIds
    // (without this a ButtonGroup could ship an undeclared action that 404s).
    nestedActionPaths: ['buttons[].action'],
    a11yContract: { role: 'group', labelledByProps: [], keyboardInteractive: true },
    classificationAware: false,
    rendered: true,
    goldenFixtures: [
      {
        buttons: [
          { label: 'Acknowledge', action: 'ack_alarm', tone: 'primary', icon: 'check' },
          { label: 'Escalate', action: 'escalate_alarm', tone: 'danger', icon: 'alert-triangle' },
        ],
      },
    ],
  },

  /* ── input: ToggleGroup ────────────────────────────────────────────────── */
  {
    slug: 'ToggleGroup',
    category: 'input',
    label: 'Toggle Group',
    description:
      'A segmented single-select control (radio-group semantics) that scopes a sibling view — e.g. switch a chart between 1h / 24h / 7d, or a board between My / Team / All. `targetId` names the component it controls; the host relays the selection through the bridge. No action mint.',
    propsSchema: z
      .object({
        label: z.string().max(80).optional(),
        targetId: z.string().min(1).max(120),
        field: z.string().min(1).max(80).default('range'),
        options: z
          .array(z.object({ label: z.string().min(1).max(40), value: z.string().min(1).max(80) }))
          .min(2)
          .max(8),
        defaultValue: z.string().max(80).optional(),
      })
      .strict(),
    bindingSlots: [],
    actionSlots: [],
    a11yContract: { role: 'radiogroup', labelledByProps: ['label'], keyboardInteractive: true },
    classificationAware: false,
    rendered: true,
    goldenFixtures: [
      {
        label: 'Range',
        targetId: 'trend_chart',
        field: 'range',
        options: [
          { label: '1h', value: '1h' },
          { label: '24h', value: '24h' },
          { label: '7d', value: '7d' },
        ],
        defaultValue: '24h',
      },
    ],
  },
];

/* ─── Registry surface ──────────────────────────────────────────────────── */

/**
 * The CLOSED registry, keyed by component slug. Lookups are O(1). Adding a
 * component is a code change here + a renderer (or stub) in @argusai/web — the
 * vocabulary is intentionally not runtime-extensible (no DB rows) so the gates
 * can guarantee a known finite surface.
 */
export const COCKPIT_LIBRARY: Readonly<Record<string, CockpitComponentContract>> =
  Object.freeze(
    Object.fromEntries(CONTRACTS.map((c) => [c.slug, c])),
  );

/** Every component slug in the catalog (sorted, frozen). */
export const COCKPIT_COMPONENT_SLUGS: readonly string[] = Object.freeze(
  CONTRACTS.map((c) => c.slug).sort(),
);

/** The slug set as a fast membership check for the CockpitSpec schema + gates. */
const SLUG_SET: ReadonlySet<string> = new Set(COCKPIT_COMPONENT_SLUGS);

/** True iff `slug` is a catalog component. */
export function isCockpitComponent(slug: string): boolean {
  return SLUG_SET.has(slug);
}

/** Get a contract by slug, or undefined if not in the catalog. */
export function getCockpitComponent(
  slug: string,
): CockpitComponentContract | undefined {
  return COCKPIT_LIBRARY[slug];
}

/** The slug of the only legal root component. */
export const COCKPIT_ROOT_SLUG = 'CockpitContainer' as const;

/** Slugs that ship a real renderer (the whole catalog — both render paths). */
export const RENDERED_COMPONENT_SLUGS: readonly string[] = Object.freeze(
  CONTRACTS.filter((c) => c.rendered).map((c) => c.slug).sort(),
);
